import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Library } from "lucide-react";
import { api } from "@/lib/api/client";
import { PageHeader } from "@/components/ui/PageHeader";
import { downloadFile } from "@/lib/api/download";
import { DataTable, type Column } from "@/components/ui/DataTable";
import { Pagination, DEFAULT_PAGE_SIZE } from "@/components/ui/Pagination";
import { SearchPanel, filterInputClass } from "@/components/ui/SearchPanel";
import { EntityPickerField, type PickedEntity } from "@/components/ui/EntityPicker";
import { AttachmentCell, type AttachmentRef } from "@/components/ui/AttachmentCell";
import { FilePreviewModal } from "@/components/ui/FilePreviewModal";

/**
 * Thư viện văn bản — văn bản đã công bố. Bảng `document_library` của vpdt rỗng;
 * hệ cũ đọc `document_published` qua DocumentAction/actionSearchDocViewLibrary
 * (163 bản ghi). Cột lấy theo lưới hệ cũ.
 */
type LibraryDoc = {
  DOCUMENT_ID: number;
  TITLE: string | null;
  CODE: string | null;
  REGISTER_NUMBER: string | null;
  CONTENT: string | null;
  TYPE_NAME: string | null;
  BUILT_GROUP_NAME: string | null;
  EFFECTIVE_DATE: string | null;
  EXPIRED_DATE: string | null;
  FOLDER_ID: number | null;
  FOLDER_NAME: string | null;
  mainFile: AttachmentRef | null;
};

const day = (v: string | null) => (v ? v.slice(0, 10) : "—");

async function fetchLibrary(page: number, pageSize: number, f: Filters) {
  const { data } = await api.get("/document/library/list", {
    params: { page, pageSize, ...toParams(f) },
  });
  return (data.data ?? data) as { items: LibraryDoc[]; total: number };
}

type Filters = {
  code: string;
  typeId: string;
  content: string;
  builtGroupId: string;
  effectiveFrom: string;
  effectiveTo: string;
  expiredFrom: string;
  expiredTo: string;
  effective: string;
  rangePublished: string;
};
const EMPTY: Filters = {
  code: "",
  typeId: "",
  content: "",
  builtGroupId: "",
  effectiveFrom: "",
  effectiveTo: "",
  expiredFrom: "",
  expiredTo: "",
  effective: "",
  rangePublished: "",
};
const toParams = (f: Filters) =>
  Object.fromEntries(Object.entries(f).filter(([, v]) => v !== "")) as Record<string, string>;

export function DocumentLibraryPage() {
  const nav = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [filters, setFilters] = useState<Filters>(EMPTY);
  const [applied, setApplied] = useState<Filters>(EMPTY);
  const [org, setOrg] = useState<PickedEntity | null>(null);
  const [previewFile, setPreviewFile] = useState<AttachmentRef | null>(null);

  const { data: types } = useQuery({
    queryKey: ["document-types"],
    queryFn: async () => {
      const { data } = await api.get("/document-type/list");
      return (data.data ?? data) as { typeId: number; typeName: string | null }[];
    },
  });

  const { data, isLoading } = useQuery({
    queryKey: ["document-library", page, pageSize, applied],
    queryFn: () => fetchLibrary(page, pageSize, applied),
  });

  const set = <K extends keyof Filters>(k: K, v: Filters[K]) =>
    setFilters((f) => ({ ...f, [k]: v }));
  const text = (label: string, key: keyof Filters) => ({
    label,
    control: (
      <input
        className={filterInputClass}
        value={filters[key]}
        onChange={(e) => set(key, e.target.value)}
      />
    ),
  });
  const date = (label: string, key: keyof Filters) => ({
    label,
    control: (
      <input
        type="date"
        className={filterInputClass}
        value={filters[key]}
        onChange={(e) => set(key, e.target.value)}
      />
    ),
  });
  const submit = () => {
    setApplied({ ...filters, builtGroupId: org ? String(org.id) : "" });
    setPage(1);
  };

  const columns: Column<LibraryDoc>[] = [
    {
      key: "stt",
      header: "STT",
      width: "56px",
      align: "center",
      render: (_r, i) => (
        <span className="text-muted-foreground tabular-nums">{(page - 1) * pageSize + i + 1}</span>
      ),
    },
    {
      key: "id",
      header: "ID",
      width: "80px",
      align: "center",
      render: (r) => <span className="tabular-nums text-muted-foreground">{r.DOCUMENT_ID}</span>,
    },
    {
      key: "reg",
      header: "Số, ký hiệu",
      width: "160px",
      render: (r) => (
        <span className="font-mono text-xs">
          <span className="font-medium">{r.REGISTER_NUMBER ?? "—"}</span>
          <div className="text-muted-foreground text-xxs">{r.CODE ?? ""}</div>
        </span>
      ),
    },
    {
      key: "title",
      header: "Trích yếu nội dung văn bản",
      render: (r) => <span className="font-medium">{r.TITLE ?? "—"}</span>,
    },
    { key: "type", header: "Hình thức văn bản", width: "150px", render: (r) => r.TYPE_NAME ?? "—" },
    {
      key: "org",
      header: "Đơn vị xây dựng",
      width: "200px",
      render: (r) => r.BUILT_GROUP_NAME ?? "—",
    },
    {
      key: "effective",
      header: "Ngày hiệu lực",
      width: "120px",
      render: (r) => <span className="text-sm tabular-nums">{day(r.EFFECTIVE_DATE)}</span>,
    },
    {
      key: "expired",
      header: "Ngày hết hiệu lực",
      width: "140px",
      render: (r) => <span className="text-sm tabular-nums">{day(r.EXPIRED_DATE)}</span>,
    },
    {
      key: "file",
      header: "File văn bản",
      width: "220px",
      render: (r) => (
        <AttachmentCell files={r.mainFile ? [r.mainFile] : null} onOpen={setPreviewFile} />
      ),
    },
    {
      key: "folder",
      header: "Thư mục",
      width: "160px",
      render: (r) => r.FOLDER_NAME ?? "—",
    },
  ];

  /** Nút "Xuất báo cáo" — hệ cũ có ở mọi lưới báo cáo. */
  const onExport = () =>
    downloadFile("/document/library/list/export", toParams(applied), "thu-vien-van-ban.csv");

  return (
    <div>
      <PageHeader
        title="Thư viện văn bản"
        subtitle={`${(data?.total ?? 0).toLocaleString("vi-VN")} văn bản đã công bố`}
        breadcrumb="Quản lý Văn bản › Thư viện"
      />
      <SearchPanel
        fields={[
          text("Số, ký hiệu văn bản", "code"),
          {
            label: "Hình thức văn bản",
            control: (
              <select
                className={filterInputClass}
                value={filters.typeId}
                onChange={(e) => set("typeId", e.target.value)}
              >
                <option value="">---- Tất cả ----</option>
                {(types ?? []).map((t) => (
                  <option key={t.typeId} value={t.typeId}>
                    {t.typeName ?? t.typeId}
                  </option>
                ))}
              </select>
            ),
          },
          text("Nội dung", "content"),
          {
            label: "Trạng thái hiệu lực",
            control: (
              <select
                className={filterInputClass}
                value={filters.effective}
                onChange={(e) => set("effective", e.target.value)}
              >
                <option value="">---- Tất cả ----</option>
                <option value="1">Còn hiệu lực</option>
                <option value="0">Hết hiệu lực</option>
              </select>
            ),
          },
          {
            label: "Phạm vi áp dụng",
            control: (
              <input
                className={filterInputClass}
                placeholder="ID phạm vi"
                value={filters.rangePublished}
                onChange={(e) => set("rangePublished", e.target.value)}
              />
            ),
          },
          {
            label: "Đơn vị xây dựng",
            control: <EntityPickerField kind="org" value={org} onChange={setOrg} />,
          },
          date("Ngày hiệu lực từ", "effectiveFrom"),
          date("đến", "effectiveTo"),
          date("Ngày hết hiệu lực từ", "expiredFrom"),
          date("đến", "expiredTo"),
        ]}
        onSubmit={submit}
        onExport={onExport}
      />
      <DataTable<LibraryDoc>
        columns={columns}
        rows={data?.items ?? []}
        keyBy={(r, i) => `${r.DOCUMENT_ID}-${i}`}
        isLoading={isLoading}
        onRowClick={(r) => nav(`/documents/${r.DOCUMENT_ID}`)}
        empty={
          <>
            <Library className="inline h-4 w-4 mr-1" /> Thư viện chưa có văn bản nào
          </>
        }
        dense
      />
      <Pagination
        page={page}
        pageSize={pageSize}
        total={data?.total ?? 0}
        onPageChange={setPage}
        onPageSizeChange={(n) => {
          setPageSize(n);
          setPage(1);
        }}
      />
      <FilePreviewModal
        open={previewFile !== null}
        file={previewFile}
        onClose={() => setPreviewFile(null)}
      />
    </div>
  );
}
